import { usePlatform } from '@/composables/usePlatform';
import { setLocale } from '@/i18n';
import { primeLocaleByCode } from '@/utils/primevue-locale/locale';
import { defineStore } from 'pinia';
import { usePrimeVue } from 'primevue/config';
import { ref, type Ref } from 'vue';

// Add this type at the top of your file (outside the store)
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export const useAppStore = defineStore('app', () => {
  const showPrompt = ref(false);
  const { isNative } = usePlatform();

  const primeConfig = usePrimeVue(); // safe because stores run in setup()

  // Try to get initial language from different sources in this priority:
  // 1. localStorage (returning user preference)
  // 2. Browser language if supported
  // 3. Default to 'en'
  function getInitialLanguage(): 'fr' | 'en' | 'de' | 'it' {
    const savedLanguage = localStorage.getItem('language') as 'fr' | 'en' | 'de' | 'it' | null;
    if (savedLanguage && ['fr', 'en', 'de', 'it'].includes(savedLanguage)) {
      return savedLanguage;
    }

    const browserLanguage = navigator.language || navigator.languages[0];
    const languageCode = browserLanguage?.split('-')[0];
    if (['fr', 'en', 'de', 'it'].includes(languageCode)) {
      return languageCode as 'fr' | 'en' | 'de' | 'it';
    }

    return 'en';
  }

  const appReady: Ref<boolean> = ref(false);
  const language: Ref<'fr' | 'en' | 'de' | 'it'> = ref(getInitialLanguage());

  // Apply initial language
  setLocale(language.value);
  document.documentElement.lang = language.value;
  localStorage.setItem('language', language.value);

  function setLanguage(newLanguage: 'fr' | 'en' | 'de' | 'it') {
    setLocale(newLanguage);

    primeConfig.config.locale = primeLocaleByCode[newLanguage];

    language.value = newLanguage;
    localStorage.setItem('language', newLanguage);

    document.documentElement.lang = newLanguage;
  }

  /// PWA

  const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
  const isPwaInstalled = ref(false);

  // Detect if device is on iOS
  function isIOS() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  }

  // Detect if device is in standalone mode
  function isInStandaloneMode() {
    return 'standalone' in window.navigator && (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  }

  function checkIfInstalled() {
    // Check if running in standalone mode (installed PWA)
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
    // Check for iOS standalone mode
    const isInIOSStandaloneMode = 'standalone' in window.navigator && window.navigator.standalone === true;

    isPwaInstalled.value = isInStandaloneMode || isInIOSStandaloneMode;
    return isPwaInstalled.value;
  }

  function shouldShowIOSInstructions() {
    // Show iOS instructions if:
    // 1. User is on iOS
    // 2. App is not in standalone mode
    // 3. User hasn't dismissed the instructions
    const hasSeenInstructions = localStorage.getItem('pwaPromptDismissed') === 'true';
    return isIOS() && !isInStandaloneMode() && !isNative && !hasSeenInstructions;
  }

  async function installPwa() {
    if (!deferredPrompt.value) return false;

    // Show the install prompt
    await deferredPrompt.value.prompt();
    const { outcome } = await deferredPrompt.value.userChoice;
    console.log(`User response: ${outcome}`);

    deferredPrompt.value = null;
    return outcome === 'accepted';
  }

  return {
    appReady,
    language,
    setLanguage,
    deferredPrompt,
    isPwaInstalled,
    isIOS,
    isInStandaloneMode,
    checkIfInstalled,
    installPwa,
    showPrompt,
    shouldShowIOSInstructions,
  };
});
